import axios from 'axios'

// WEB-7069 — measured shadow-billing xlsx. Posted as a blob (not RTK Query)
// so a long property list stays in the body and the file is not JSON-decoded.

const BASE_URL = '/api/v1/admin'

const SPREADSHEET_TYPE = /spreadsheet|excel|octet-stream/i

export interface ExportShadowBillingArgs {
  from: string
  to: string
  /** Omitted / empty = portfolio-wide. */
  propertyCuids?: string[]
  signal?: AbortSignal
}

const messageFromBody = (text: string): string | null => {
  if (text.trim() === '') return null
  try {
    const parsed: unknown = JSON.parse(text)
    if (parsed != null && typeof parsed === 'object') {
      const rec = parsed as { message?: unknown; error?: unknown }
      if (typeof rec.message === 'string' && rec.message !== '') return rec.message
      if (typeof rec.error === 'string' && rec.error !== '') return rec.error
    }
  } catch {
    // not JSON — fall through
  }
  return text.length <= 200 ? text : null
}

const filenameFromDisposition = (header: string | undefined, fallback: string): string => {
  if (header == null || header === '') return fallback
  const starred = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i)
  if (starred?.[1] != null && starred[1] !== '' && !starred[1].startsWith('=')) {
    try {
      return decodeURIComponent(starred[1].trim())
    } catch {
      // keep looking
    }
  }
  const quoted = header.match(/filename="([^"]+)"/)
  if (quoted?.[1] != null && quoted[1] !== '') return quoted[1]
  const unquoted = header.match(/filename=([^;]+)/)
  const raw = unquoted?.[1]?.trim()
  if (raw != null && raw !== '' && !raw.startsWith('*')) return raw
  return fallback
}

const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

const throwFromBlob = async (blob: Blob, fallback: string): Promise<never> => {
  const text = await blob.text()
  throw new Error(messageFromBody(text) ?? fallback)
}

export const exportShadowBilling = async ({
  from,
  to,
  propertyCuids,
  signal
}: ExportShadowBillingArgs): Promise<void> => {
  const body: { from: string; to: string; propertyCuids?: string[] } = { from, to }
  if (propertyCuids != null && propertyCuids.length > 0) {
    body.propertyCuids = propertyCuids
  }
  try {
    const response = await axios.post(
      `${BASE_URL}/saber/export`,
      body,
      signal != null ? { responseType: 'blob', signal } : { responseType: 'blob' }
    )
    const contentType = String(response.headers['content-type'] ?? '')
    if (contentType !== '' && !SPREADSHEET_TYPE.test(contentType)) {
      await throwFromBlob(response.data as Blob, 'Download failed. Unexpected response.')
    }
    const filename = filenameFromDisposition(
      response.headers['content-disposition'] as string | undefined,
      `saber-shadow-billing-${from}_${to}.xlsx`
    )
    triggerDownload(response.data as Blob, filename)
  } catch (err) {
    const aborted =
      (typeof axios.isCancel === 'function' && axios.isCancel(err)) ||
      (err instanceof DOMException && err.name === 'AbortError')
    if (aborted) throw err
    const data = (err as { response?: { data?: unknown } } | null)?.response?.data
    if (data instanceof Blob) {
      await throwFromBlob(data, 'Download failed. Try again.')
    }
    if (err instanceof Error) throw err
    throw new Error('Download failed. Try again.')
  }
}
